import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	Alert,
	AlertDescription,
	AlertTitle,
	Box,
	Button,
	Checkbox,
	DrawerBody,
	DrawerFooter,
	DrawerHeader,
	Flex,
	IconButton,
	Input,
	InputGroup,
	InputLeftElement,
	Text,
} from "@chakra-ui/react";
import { Trans, useTranslation } from "react-i18next";
import { v4 as uuidV4 } from "uuid";
import { Notice } from "@/components/Notice";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { ValidateRuleRequest } from "@/api/rulesLibrary";
import { colours, qoriaColours } from "@/theme/colours";
import { fonts } from "@/theme/fonts";
import { useSelectedClassroomStore } from "@/stores/v2/selectedClassroomStore";
import { useSignatureStore } from "@/stores/v2/signatureStore";
import { useClasswizeConfigStore } from "@/stores/classwizeConfigStore";
import useStore from "@/stores";
import { WINDOWS_APP_LIST_URL } from "../constants";
import { BlockAllowToggle } from "../ListForm/BlockAllowToggle";
import { RuleTargetPicker } from "../ListForm/RuleTargetPicker";
import { resolveRuleTarget } from "../ListForm/utils";
import { AppliedList, Rule, RuleAction } from "../types";
import { buildValidateRuleRequest, formatWebsiteTarget, isLockedRuleConflictError } from "../utils";
import { RulePickerTargetType } from "../ListForm/utils";
import { scrollbarProps } from "../styles";
import { ApplyScope, ClassRulePrefill, InClassRule, InClassRuleTargetType, NewInClassRule } from "./types";
import { indexEffectiveListRules, listRuleOverriddenByClassRule, persistableStudentIds, selectedStudentRowIds } from "./utils";

const EMPTY_APPLIED_LISTS: AppliedList[] = [];

export interface ClassRuleStudent {
	id: string;
	name: string;
	stableIdV1?: string;
	qoriaUserId?: string;
}

interface Props {
	onBack: () => void;
	/** When set, the form edits this in-class rule's audience only. */
	rule?: InClassRule;
	onSave?: (applyTo: ApplyScope, studentIds: string[]) => void;
	onCreate?: (rule: NewInClassRule) => void;
	/** Create mode only: starting target and audience for the new rule. */
	prefill?: ClassRulePrefill;
	students?: ClassRuleStudent[];
	/** Disables Create/Save while a class-rule mutation is in flight. */
	isSaving?: boolean;
	/** Lists already applied to this class, used to warn when a whole-class rule would override one. */
	appliedLists?: AppliedList[];
	/** Checks the chosen website or signature against school filter locked rules before create. */
	validateRule?: (body: ValidateRuleRequest) => Promise<void>;
}

export function ClassRuleForm(props: Props): JSX.Element {
	const { t } = useTranslation();
	const { onBack, rule, onSave, onCreate, prefill, isSaving = false, appliedLists = EMPTY_APPLIED_LISTS, validateRule } = props;
	const isEditing = rule !== undefined;
	const lookupName = useSignatureStore((state) => state.lookupName);
	const findAppControl = useSignatureStore((state) => state.findAppControl);
	const blockingRulesOnly = useClasswizeConfigStore((state) => state.data.create_blocking_rules_only) === true;
	const includeAppControls = useStore((state) => state.featureToggles.isNativeAppControlsEnabled());
	const storeStudents = useSelectedClassroomStore((state) => state.students);
	const students = useMemo(
		() =>
			props.students ??
			(storeStudents ?? []).map((student) => ({
				id: student.stableIdV1,
				stableIdV1: student.stableIdV1,
				qoriaUserId: student.qoriaUserId,
				name: `${student.firstName} ${student.lastName}`.trim(),
			})),
		[props.students, storeStudents]
	);
	const [initialSelectedStudentIds] = useState<string[]>(() =>
		rule
			? selectedStudentRowIds(students, rule.studentIds, rule.type)
			: selectedStudentRowIds(students, prefill?.studentIds ?? [], prefill?.type)
	);
	const [target, setTarget] = useState(rule ? "" : (prefill?.target ?? ""));
	const [targetType, setTargetType] = useState<RulePickerTargetType | undefined>(rule ? undefined : prefill?.type);
	const [action, setAction] = useState<RuleAction>("block");
	const [applyTo, setApplyTo] = useState<ApplyScope>(() => {
		if (rule) {
			return rule.applyTo;
		}
		return initialSelectedStudentIds.length > 0 ? "students" : "class";
	});
	const [studentQuery, setStudentQuery] = useState("");
	const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>(initialSelectedStudentIds);
	const isAppControlAudience = rule?.type === "APP_CONTROL" || targetType === "APP_CONTROL";
	let ruleLabel = "";
	if (rule) {
		ruleLabel = formatWebsiteTarget(rule.target);
		if (rule.type === "SIGNATURE") {
			ruleLabel = lookupName(rule.target);
		} else if (rule.type === "APP_CONTROL") {
			ruleLabel = findAppControl(rule.target)?.name ?? rule.target;
		}
	}
	const visibleStudents = students.filter((student) => student.name.toLowerCase().includes(studentQuery.trim().toLowerCase()));

	function handleTargetChange(value: string, optionType?: RulePickerTargetType): void {
		setTarget(value);
		setTargetType(value === "" ? undefined : optionType);
		if (optionType === "APP_CONTROL") {
			setSelectedStudentIds((current) => current.slice(0, 1));
		}
	}

	function handleSelectApplyTo(scope: ApplyScope): void {
		setApplyTo(scope);
	}

	function handleToggleStudent(id: string): void {
		if (isAppControlAudience) {
			setSelectedStudentIds((current) => (current.includes(id) ? [] : [id]));
			return;
		}

		setSelectedStudentIds((current) => (current.includes(id) ? current.filter((studentId) => studentId !== id) : [...current, id]));
	}

	const persistType: InClassRuleTargetType = isAppControlAudience ? "APP_CONTROL" : (rule?.type ?? "URL");
	const persistableIds = persistableStudentIds(students, selectedStudentIds, persistType);
	const hasValidStudentSelection = isAppControlAudience ? persistableIds.length === 1 : persistableIds.length > 0;
	const canSubmitAudience = applyTo === "class" || hasValidStudentSelection;
	const canCreate = target.trim().length > 0 && canSubmitAudience && !isSaving;
	const listRuleIndex = useMemo(() => indexEffectiveListRules(appliedLists), [appliedLists]);
	const savedAction: RuleAction = blockingRulesOnly ? "block" : action;
	const resolvedTarget = useMemo(() => {
		if (isEditing || targetType === "APP_CONTROL" || target.trim() === "") {
			return undefined;
		}
		if (targetType === "SIGNATURE") {
			return { target, type: "SIGNATURE" as const };
		}
		return resolveRuleTarget(target);
	}, [isEditing, target, targetType]);
	const listOverride = useMemo(() => {
		if (applyTo !== "class" || resolvedTarget === undefined) {
			return undefined;
		}
		return listRuleOverriddenByClassRule(listRuleIndex, { target: resolvedTarget.target, action: savedAction });
	}, [applyTo, listRuleIndex, resolvedTarget, savedAction]);
	const overrideLabel =
		resolvedTarget === undefined
			? ""
			: resolvedTarget.type === "SIGNATURE"
				? lookupName(resolvedTarget.target)
				: formatWebsiteTarget(resolvedTarget.target);
	const validateRuleRef = useRef(validateRule);
	validateRuleRef.current = validateRule;
	const [lockedRuleWarning, setLockedRuleWarning] = useState(false);

	useEffect(() => {
		const checkLockedRule = validateRuleRef.current;
		if (isEditing || resolvedTarget === undefined || checkLockedRule === undefined) {
			setLockedRuleWarning(false);
			return;
		}

		let cancelled = false;
		const candidate: Rule = {
			id: "class-rule-preview",
			target: resolvedTarget.target,
			type: resolvedTarget.type,
			action: savedAction,
		};

		setLockedRuleWarning(false);

		checkLockedRule(buildValidateRuleRequest(candidate, []))
			.then(() => {
				if (!cancelled) {
					setLockedRuleWarning(false);
				}
			})
			.catch((error: unknown) => {
				if (!cancelled) {
					setLockedRuleWarning(isLockedRuleConflictError(error));
				}
			});

		return () => {
			cancelled = true;
		};
	}, [isEditing, resolvedTarget, savedAction]);
	const canSave = isEditing && canSubmitAudience && !isSaving;

	function handleSave(): void {
		if (!canSave || !onSave || rule === undefined) {
			return;
		}

		if (applyTo === "class") {
			onSave(applyTo, []);
			return;
		}

		onSave(applyTo, persistableIds);
	}

	function handleCreate(): void {
		if (!canCreate || !onCreate) {
			return;
		}

		let resolved: { target: string; type: InClassRuleTargetType } = resolveRuleTarget(target);
		if (targetType === "APP_CONTROL") {
			if (!includeAppControls) {
				return;
			}
			resolved = { target, type: "APP_CONTROL" };
		} else if (targetType === "SIGNATURE") {
			resolved = { target, type: "SIGNATURE" };
		}

		let studentIds: string[] = [];
		if (applyTo !== "class") {
			studentIds = persistableIds;
		}

		onCreate({
			id: uuidV4(),
			target: resolved.target,
			type: resolved.type,
			action: blockingRulesOnly ? "block" : action,
			applyTo,
			studentIds,
		});
	}

	return (
		<>
			<DrawerHeader borderBottom="1px solid" borderColor={qoriaColours.brand.neutral[100]} px={6} py={4}>
				<Flex align="center" gap={3}>
					<IconButton
						aria-label={t("rulesLibrary.form.back")}
						variant="ghost"
						onClick={onBack}
						icon={<ChakraFAIcon icon="fa-arrow-left" variant="solid" color={colours.text.regular} boxSize={4} />}
					/>
					<Text fontFamily={fonts.body} fontSize="xl" fontWeight={500} color={colours.text.title}>
						{isEditing ? t("rulesLibrary.activeRules.editClassRuleTitle") : t("rulesLibrary.activeRules.classRuleTitle")}
					</Text>
				</Flex>
			</DrawerHeader>
			<DrawerBody px={6} py={5}>
				{isEditing ? (
					<Text fontFamily={fonts.body} fontSize="md" fontWeight={500} color={colours.text.title} mb={6}>
						{ruleLabel}
					</Text>
				) : (
					<>
						<Text fontFamily={fonts.body} fontSize="md" fontWeight={400} color={colours.text.paragraph.light} mb={6}>
							{t("createPolicy.introControlAccess")}
							{includeAppControls ? (
								<>
									{" "}
									<Trans
										i18nKey="createPolicy.windowsAppListHint"
										components={{
											appListLink: (
												<Box
													as="a"
													href={WINDOWS_APP_LIST_URL}
													target="_blank"
													rel="noreferrer"
													color={colours.brand[500]}
													textDecoration="underline"
												/>
											),
										}}
									/>
								</>
							) : null}
						</Text>
						<Box border="1px solid" borderColor={qoriaColours.brand.neutral[100]} rounded="xl" p={4} mb={6}>
							<Text fontFamily={fonts.body} fontSize="sm" fontWeight={700} color={colours.text.regular} mb={3}>
								{t("rulesLibrary.form.addItems")}
							</Text>
							<Box mb={3}>
								<RuleTargetPicker includeAppControls={includeAppControls} value={target} onChange={handleTargetChange} />
							</Box>
							<BlockAllowToggle value={action} onChange={setAction} />
							{lockedRuleWarning && (
								<Alert
									status="warning"
									mt={3}
									px="14px"
									py="12px"
									gap="10px"
									borderRadius="6px"
									bg={qoriaColours.brand.warning[200]}
									data-testid="class-rule-locked-filter"
								>
									<ChakraFAIcon icon="fa-triangle-exclamation" variant="solid" boxSize="16px" color={qoriaColours.brand.warning[600]} />
									<Flex direction="column" gap="4px" flex={1} minW={0}>
										<AlertTitle fontSize="14px" fontWeight={500} lineHeight="20px" color={qoriaColours.brand.neutral[800]}>
											{t("createPolicy.lockedRuleBannerTitle")}
										</AlertTitle>
										<AlertDescription fontSize="14px" fontWeight={400} lineHeight="19px" pb={0} color={qoriaColours.brand.neutral[800]}>
											{t("createPolicy.lockedRuleBannerMessage")}
										</AlertDescription>
									</Flex>
								</Alert>
							)}
							{listOverride !== undefined && (
								<Notice status="info" mt={3} data-testid="class-rule-list-override">
									<Text fontFamily={fonts.body} fontSize="sm" color={colours.text.title}>
										<Trans
											i18nKey={
												listOverride.listAction === "allow"
													? "rulesLibrary.activeRules.overridesListRuleAllowed"
													: "rulesLibrary.activeRules.overridesListRuleBlocked"
											}
											values={{ target: overrideLabel, list: listOverride.listTitle }}
											components={{ bold: <Box as="span" fontWeight={700} /> }}
										/>
									</Text>
								</Notice>
							)}
						</Box>
					</>
				)}
				<Text fontFamily={fonts.body} fontSize="md" fontWeight={400} color={colours.text.regular} mb={3}>
					{t("rulesLibrary.activeRules.whoDoesThisRuleAffect")}
				</Text>
				<Flex gap={3} align="stretch">
					<ApplyToCard
						selected={applyTo === "class"}
						onSelect={() => handleSelectApplyTo("class")}
						icon="fa-users"
						label={t("rulesLibrary.activeRules.wholeClass")}
					/>
					<ApplyToCard
						selected={applyTo === "students"}
						onSelect={() => handleSelectApplyTo("students")}
						icon="fa-user"
						label={t("rulesLibrary.activeRules.specificStudents")}
					/>
				</Flex>
				{applyTo === "students" && (
					<Box mt={4} border="1px solid" borderColor={qoriaColours.brand.neutral[100]} rounded="xl" overflow="hidden">
						<Flex align="center" gap={3} px={4} py={3} borderBottom="1px solid" borderColor={qoriaColours.brand.neutral[100]}>
							<InputGroup>
								<InputLeftElement pointerEvents="none" height="full">
									<ChakraFAIcon icon="fa-magnifying-glass" variant="regular" color={colours.text.regular} boxSize={4} />
								</InputLeftElement>
								<Input
									value={studentQuery}
									onChange={(event) => setStudentQuery(event.target.value)}
									placeholder={t("rulesLibrary.activeRules.searchStudentsPlaceholder")}
									fontFamily={fonts.body}
									pl={10}
								/>
							</InputGroup>
							<Text fontFamily={fonts.body} fontSize="sm" fontWeight={400} color={colours.text.regular} flexShrink={0}>
								{t("rulesLibrary.activeRules.studentsSelected", { count: selectedStudentIds.length })}
							</Text>
						</Flex>
						<Box maxH="280px" overflowY="auto" overscrollBehavior="contain" data-testid="class-rule-student-list" sx={scrollbarProps}>
							{visibleStudents.map((student) => (
								<Flex
									key={student.id}
									align="center"
									px={4}
									py={3}
									cursor="pointer"
									_hover={{ bg: qoriaColours.brand.neutral[50] }}
									onClick={() => handleToggleStudent(student.id)}
									data-testid={`class-rule-student-row-${student.id}`}
								>
									<Checkbox
										isChecked={selectedStudentIds.includes(student.id)}
										onChange={() => handleToggleStudent(student.id)}
										onClick={(event) => event.stopPropagation()}
										fontFamily={fonts.body}
										fontWeight={400}
										color={colours.text.title}
										spacing={3}
										pointerEvents="none"
									>
										{student.name}
									</Checkbox>
								</Flex>
							))}
						</Box>
					</Box>
				)}
			</DrawerBody>
			<DrawerFooter borderTop="1px solid" borderColor={qoriaColours.brand.neutral[100]} gap={2} justifyContent="stretch">
				<Button variant="neutralOutline" onClick={onBack} flex={1}>
					{t("rulesLibrary.activeRules.cancel")}
				</Button>
				{isEditing ? (
					<Button
						onClick={handleSave}
						isDisabled={!canSave}
						isLoading={isSaving}
						leftIcon={<ChakraFAIcon icon="fa-floppy-disk" variant="regular" boxSize={4} />}
						flex={1}
					>
						{t("rulesLibrary.activeRules.save")}
					</Button>
				) : (
					<Button
						onClick={handleCreate}
						isDisabled={!canCreate}
						isLoading={isSaving}
						leftIcon={<ChakraFAIcon icon="fa-plus" variant="solid" boxSize={3.5} />}
						flex={1}
					>
						{t("rulesLibrary.activeRules.createRule")}
					</Button>
				)}
			</DrawerFooter>
		</>
	);
}

function ApplyToCard(props: { selected: boolean; onSelect: () => void; icon: string; label: string }): JSX.Element {
	const { selected, onSelect, icon, label } = props;

	return (
		<Button
			variant="unstyled"
			flex={1}
			height="auto"
			py={4}
			px={4}
			rounded="xl"
			border="2px solid"
			borderColor={selected ? colours.text.title : qoriaColours.brand.neutral[100]}
			onClick={onSelect}
			aria-pressed={selected}
			whiteSpace="normal"
			textAlign="left"
		>
			<Flex align="flex-start" gap={3}>
				<Flex
					align="center"
					justify="center"
					boxSize={5}
					rounded="sm"
					bg={selected ? colours.text.title : "transparent"}
					border="2px solid"
					borderColor={selected ? colours.text.title : qoriaColours.brand.neutral[200]}
					flexShrink={0}
					mt="2px"
				>
					{selected && <ChakraFAIcon icon="fa-check" variant="solid" color="white" boxSize={3} />}
				</Flex>
				<ChakraFAIcon icon={icon} variant="solid" color={colours.text.regular} boxSize={4} mt="2px" flexShrink={0} />
				<Box>
					<Text fontFamily={fonts.body} fontWeight={500} color={colours.text.title}>
						{label}
					</Text>
				</Box>
			</Flex>
		</Button>
	);
}
